import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Button, CircularProgress, Menu, MenuItem, SxProps, Theme } from "@mui/material";
import type { JSX } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import "@/components/valuationReportGenerationPanel/translations";

interface ReportDownloadButtonProps {
  label?: string;
  onPdfAction?: () => void;
  onWordDocumentAction?: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  dataCy?: string;
  sx?: SxProps<Theme>;
}

export const ReportDownloadButton = ({
  label,
  onPdfAction,
  onWordDocumentAction,
  isLoading = false,
  isDisabled = false,
  dataCy = "valuation-item-download-report",
  sx,
}: ReportDownloadButtonProps): JSX.Element => {
  const { t } = useTranslation("ValuationReportGenerationPanel");
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const handleMenuItemClick = (callback?: () => void) => (): void => {
    callback?.();
    setMenuAnchorEl(null);
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        endIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <ExpandMoreIcon />}
        onClick={(event) => setMenuAnchorEl(event.currentTarget)}
        disabled={isDisabled || isLoading || (!onPdfAction && !onWordDocumentAction)}
        data-cy={dataCy}
        sx={[
          {
            "& .MuiButton-endIcon": {
              position: "absolute",
              right: "16px",
            },
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {label ?? t("reportPanel.table.downloadButton")}
      </Button>
      <Menu anchorEl={menuAnchorEl} open={isMenuOpen} onClose={() => setMenuAnchorEl(null)}>
        <MenuItem onClick={handleMenuItemClick(onPdfAction)} disabled={!onPdfAction} data-cy={dataCy + "-pdf"}>
          {t("reportPanel.table.downloadAsPdf")}
        </MenuItem>
        <MenuItem
          onClick={handleMenuItemClick(onWordDocumentAction)}
          disabled={!onWordDocumentAction}
          data-cy={dataCy + "-docx"}
        >
          {t("reportPanel.table.downloadAsWordDocument")}
        </MenuItem>
      </Menu>
    </>
  );
};
